import type { ColorByConfig, ColorByTarget } from '../types';
import { camelizeDeep } from './saved-well-filter-definition';

/**
 * Round-trip helpers for a persisted color-by rule's `definition` jsonb. A
 * color-by rule is a `TypeCurve::AnalogWellFilter` row with `kind='color'`
 * whose `name` is the client `ruleId` and whose `definition` carries the rule
 * config. On WRITE the backend deep-underscores the keys; on READ jbuilder
 * camelizes only the top-level response keys, so the nested `definition` reads
 * back snake_case and must be {@link camelizeDeep}d first.
 */

// ColorByConfig -> `definition` payload (camelCase; backend underscores keys).
// `valueColors` is stored as a `rules` array [{value,color}] to avoid
// `deep_transform_keys` mangling on multi-word value strings used as keys.
export function colorByConfigToDefinition(
  config: ColorByConfig
): Record<string, unknown> {
  const rules = Object.entries(config.valueColors).map(([value, color]) => ({
    value,
    color,
  }));
  return {
    attribute: config.attribute,
    selectedValues: config.selectedValues,
    rules,
    applyTo: config.applyTo,
  };
}

// Stored row (name + disabled + definition) -> ColorByConfig. Definition keys
// arrive snake_case so they must be camelized first.
export function definitionToColorByConfig(
  name: string,
  disabled: boolean,
  definition: Record<string, unknown>
): ColorByConfig {
  const d = camelizeDeep(definition ?? {}) as {
    attribute?: string;
    selectedValues?: string[];
    rules?: { value: string; color: string }[];
    applyTo?: Record<ColorByTarget, boolean>;
  };
  const valueColors: Record<string, string> = {};
  for (const rule of d.rules ?? []) {
    valueColors[rule.value] = rule.color;
  }
  return {
    ruleId: name,
    attribute: d.attribute ?? '',
    selectedValues: d.selectedValues ?? [],
    valueColors,
    applyTo: d.applyTo ?? {
      map: false,
      probitPlot: false,
      monthlyVolumes: false,
      ratios: false,
      crossPlot: false,
    },
    enabled: !disabled,
  };
}
